import store from 'redux/store';
import { deleteTrigger } from 'redux/slices/triggersSlice';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditTrigger } from './edit/showEditTrigger';

type TriggerEntity = Extract<ActionEntity, { type: 'trigger' }>;

const isAllTrigger = (entities: readonly ActionEntity[]): entities is readonly TriggerEntity[] => entities.every((entity) => entity.type === 'trigger');

/**
 * Action definitions for triggers. `createTrigger` / `addToTransportTrigger` are
 * scoped; `edit` (single-only) and `delete` are contextual. Delete goes through
 * the shared confirm+delete executor (replacing the page's old window.confirm).
 */
export const triggerActionDefinitions: ActionDefinitions = [
  {
    id: 'createTrigger',
    scope: 'type',
    label: 'New Trigger',
    resolve: (ctx) => () => void showEditTrigger({ mode: 'create', createIn: ctx.createIn }).then(ctx.refresh),
  },
  {
    id: 'edit',
    label: 'Edit',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllTrigger(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => void showEditTrigger({ mode: 'edit', id: only.id }).then(ctx.refresh);
    },
  },
  {
    id: 'delete',
    label: 'Delete',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllTrigger(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      return deleteExecutor(entities, (entity) => store.dispatch(deleteTrigger(entity.id)), {
        refresh,
        confirmBody: `Are you sure you want to delete ${entities.length === 1 ? 'this trigger' : `these ${entities.length} triggers`}?`,
      });
    },
  },
  {
    id: 'addToTransportTrigger',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllTrigger(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'trigger',
        entities.map((entity) => entity.id),
        { objectLabel: single ? `${single.zoneName || '-'} | ${single.name}` : `${entities.length} triggers` }
      );
    },
  },
];
